import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { api, type SettingItem } from '@/api/client';
import { ErrorBox, Loading, PageTitle } from '@/components/ui';

type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

interface ClanEmblem {
  id: string;
  name: string;
  imageUrl: string | null;
  rarity: Rarity;
  priceGems: number;
  special: boolean;
  awardRank: number | null;
  isActive: boolean;
}

async function req<T>(method: string, path: string, body?: unknown, isForm = false): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  if (!isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(path, {
    method, headers,
    body: isForm ? (body as FormData) : body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let d = '';
    try { const j = await res.json(); d = j?.message ?? j?.error ?? ''; } catch { /* */ }
    throw new Error(d || `HTTP ${res.status}`);
  }
  return res.json();
}

export function ClanEmblemsPage() {
  const [items, setItems] = useState<ClanEmblem[] | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [savingFeature, setSavingFeature] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const [emblemsResponse, settingsResponse] = await Promise.all([
        req<{ items: ClanEmblem[] }>('GET', '/admin/clan-emblems/'),
        api.settings(),
      ]);
      setItems(emblemsResponse.items);
      setEnabled(
        Boolean(
          settingsResponse.items.find(
            (setting: SettingItem) => setting.key === 'clan.enabled',
          )?.value,
        ),
      );
      setErr(null);
    } catch (error) {
      setErr(String(error));
    }
  };

  useEffect(() => { void load(); }, []);

  if (err && !items) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  async function toggleFeature(): Promise<void> {
    setSavingFeature(true);
    setErr(null);
    try {
      await api.setSetting('clan.enabled', !enabled);
      await load();
    } catch (error) {
      setErr(String(error));
    } finally {
      setSavingFeature(false);
    }
  }

  async function createNew(special: boolean) {
    setCreating(true);
    try {
      await req('POST', '/admin/clan-emblems/', {
        name: special ? 'New Season Logo' : 'New Clan Logo',
        rarity: special ? 'legendary' : 'epic',
        special,
        awardRank: special ? 1 : null,
      });
      await load();
    } catch (e: any) { alert(e?.message); }
    setCreating(false);
  }

  const buyable = items.filter((i) => !i.special);
  const awards = items.filter((i) => i.special);

  return (
    <div>
      <PageTitle title="Clan" />
      {err && <ErrorBox text={err} />}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div>
            <div className="display" style={{ fontSize: 18 }}>
              MASTER FEATURE FLAG
            </div>
            <div style={{ color: 'var(--ink-muted)', fontSize: 12, marginTop: 4 }}>
              {enabled
                ? 'Clan is visible. API, chat, and Clan War are active.'
                : 'Clan is hidden. Data is preserved while all Clan actions are blocked.'}
            </div>
          </div>
          <button
            className={`btn ${enabled ? 'btn-danger' : 'btn-primary'}`}
            disabled={savingFeature}
            onClick={() => void toggleFeature()}
          >
            {savingFeature
              ? 'SAVING…'
              : enabled
                ? 'DISABLE CLAN'
                : 'ENABLE CLAN'}
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--ink-muted)', background: 'rgba(155,93,229,0.08)', borderColor: 'rgba(155,93,229,0.25)' }}>
        Premium clan emblems. Upload a square PNG/WebP logo (auto-optimized to 256px WebP).
        <b> Buyable</b> logos are bought with gems &amp; owned by the clan forever.
        <b> Season-reward</b> logos (set <b>award rank 1/2/3</b>) are granted to the Top 1/2/3 clans
        each weekly season and revoked at the next reset — not for sale.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => createNew(false)} disabled={creating}>+ BUYABLE LOGO</button>
        <button className="btn" onClick={() => createNew(true)} disabled={creating}>+ SEASON REWARD</button>
      </div>

      <div className="tag" style={{ marginBottom: 8, color: 'var(--c-purple)' }}>BUYABLE (gems)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {buyable.map((s) => <EmblemRow key={s.id} item={s} onChanged={load} />)}
        {buyable.length === 0 && <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>No buyable logos yet.</div>}
      </div>

      <div className="tag" style={{ marginBottom: 8, color: 'var(--c-yellow)' }}>SEASON REWARDS (Top 1/2/3)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {awards.map((s) => <EmblemRow key={s.id} item={s} onChanged={load} />)}
        {awards.length === 0 && <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>No season-reward logos yet.</div>}
      </div>
    </div>
  );
}

function EmblemRow({ item, onChanged }: { item: ClanEmblem; onChanged: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(item.name);
  const [rarity, setRarity] = useState<Rarity>(item.rarity);
  const [priceGems, setPriceGems] = useState(item.priceGems);
  const [special, setSpecial] = useState(item.special);
  const [awardRank, setAwardRank] = useState<number>(item.awardRank ?? 1);
  const [isActive, setIsActive] = useState(item.isActive);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await req('PATCH', `/admin/clan-emblems/${item.id}`, {
        name, rarity, priceGems, special,
        awardRank: special ? awardRank : null,
        isActive,
      });
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function del() {
    if (!confirm(`Delete "${item.name}"? Clans who own it lose it.`)) return;
    setBusy(true);
    try { await req('DELETE', `/admin/clan-emblems/${item.id}`); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function upload(f: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await req('POST', `/admin/clan-emblems/${item.id}/image`, fd, true);
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }

  return (
    <div className="card" style={{ padding: 14, display: 'flex', gap: 14, flexWrap: 'wrap', opacity: isActive ? 1 : 0.6 }}>
      <div style={{ width: 56, height: 56, background: 'rgba(0,0,0,0.3)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {item.imageUrl
          ? <img src={item.imageUrl} alt="" style={{ width: 48, height: 48, objectFit: 'contain' }} />
          : <span style={{ fontSize: 9, color: 'var(--ink-dim)' }}>no img</span>}
      </div>

      <div style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={rarity} onChange={(e) => setRarity(e.target.value as Rarity)} style={{ flex: 1 }}>
            {['common', 'rare', 'epic', 'legendary'].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {!special && (
            <input type="number" value={priceGems} onChange={(e) => setPriceGems(+e.target.value)} placeholder="gems" style={{ width: 90 }} />
          )}
          {special && (
            <select value={awardRank} onChange={(e) => setAwardRank(+e.target.value)} style={{ width: 110 }}>
              <option value={1}>Top 1</option>
              <option value={2}>Top 2</option>
              <option value={3}>Top 3</option>
            </select>
          )}
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 12 }}>
          <label><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> active</label>
          <label><input type="checkbox" checked={special} onChange={(e) => setSpecial(e.target.checked)} /> season reward</label>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }}>
        <input type="file" accept="image/png,image/webp,image/gif" ref={fileRef} style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
        <button className="btn" onClick={() => fileRef.current?.click()} disabled={busy}>{item.imageUrl ? 'REPLACE IMG' : 'UPLOAD IMG'}</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>SAVE</button>
        <button className="btn" onClick={del} disabled={busy} style={{ color: 'var(--c-red)' }}>DELETE</button>
      </div>
    </div>
  );
}
